import { cn } from '@promanage/ui';
import { useRailBadges } from '../alerts/useRailBadges';

/**
 * How many emails assigned to the person looking they have not opened.
 *
 * ONE NUMBER, WHEREVER IT IS SHOWN — the rail, the phone's bar, the Inbox tab and the inbox's own
 * subtitle all read the rail's one request (`useRailBadges`), so they cannot disagree. It is asked
 * again every minute because the rail is on every page: the point of a badge there is to say mail
 * has arrived to somebody who is not looking at the inbox.
 *
 * Undefined until the first answer.
 */
export function useUnreadMail(): number | undefined {
  return useRailBadges()?.unreadMail;
}

const TONE = {
  accent: 'bg-accent text-accent-ink',
  danger: 'bg-danger text-white',
} as const;

/**
 * A count as the small badge it is everywhere — on a rail icon, at the end of a row, on a tab.
 * Nothing at all at zero: a badge that is always there stops being read.
 *
 * `99+` past two digits: it sits on a 20px icon, and the difference between 140 and 150 unread is
 * not something anybody acts on from the rail.
 *
 * `label` is what it is a count OF, said to a screen reader ("3 unread"). Leave it out where the
 * control the badge sits on already says so in its own name, and the badge is then decoration.
 */
export function CountBadge({
  count,
  tone = 'accent',
  label,
  className,
}: {
  count: number;
  tone?: keyof typeof TONE | undefined;
  label?: string | undefined;
  className?: string | undefined;
}) {
  if (count <= 0) return null;
  return (
    <span
      {...(label === undefined ? { 'aria-hidden': true } : { 'aria-label': label })}
      className={cn(
        'inline-flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full px-1 text-[0.625rem] leading-none font-semibold tabular-nums',
        TONE[tone],
        className,
      )}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

/** The unread-mail number: blue, and it says what it counts. */
export function UnreadBadge({
  count,
  className,
}: {
  count: number;
  className?: string | undefined;
}) {
  return <CountBadge count={count} label={`${String(count)} unread`} className={className} />;
}

/** The badge for a place in the navigation that counts unread mail. */
export function UnreadMailBadge({ className }: { className?: string | undefined }) {
  return <UnreadBadge count={useUnreadMail() ?? 0} className={className} />;
}
